import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#4A0F1E] flex items-center justify-center p-4" data-testid="admin-login">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 mb-4">
            <svg width="32" height="32" viewBox="0 0 36 36" fill="none">
              <circle cx="18" cy="18" r="17" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.5)" strokeWidth="1"/>
              <ellipse cx="18" cy="19" rx="6" ry="4" fill="rgba(255,255,255,0.5)"/>
              <circle cx="18" cy="15" r="3" fill="white" opacity="0.9"/>
            </svg>
          </div>
          <h1 className="font-playfair text-2xl font-bold text-white">Apni Dhani Bakery</h1>
          <p className="text-white/60 text-sm mt-1">Admin Panel</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-2xl">
          <h2 className="font-playfair text-xl font-bold text-[#1F1410] mb-5">Sign In</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1.5">Email</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A63]" />
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="admin@apnidhanibakery.com"
                  required
                  className="w-full pl-9 pr-4 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] focus:ring-1 focus:ring-[#7A1125]/20"
                  data-testid="admin-email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1.5">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A63]" />
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Enter password"
                  required
                  className="w-full pl-9 pr-10 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] focus:ring-1 focus:ring-[#7A1125]/20"
                  data-testid="admin-password-input"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A6A63]">
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#7A1125] hover:bg-[#8B1A2B] text-white font-semibold py-3 rounded-xl transition-colors mt-2"
              data-testid="admin-login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
